import NeptuneElement from '../../NeptuneElement.js';
import { i18nMixin } from '../../../utils/i18nMixin.js';
import { i18n } from '../../../utils/i18n.js';

class Switch extends i18nMixin(NeptuneElement) {

    templateMarkup = `
        <div>
            <label>
                <span class="font-size-md">
                    <slot name="label"></slot>
                </span>
                <span>
                    <input type="checkbox" role="switch" />
                    <span></span>
                    <slot name="state-text" class="font-size-md"></slot>
                </span>
            </label>
            <div>
                <span class="font-size-sm"></span>
                <ul class="margin-0 padding-0 list-custom font-size-sm" aria-live="polite"></ul>
            </div>
        </div>
    `;

    componentCssUrl = '/src/components/form/switch/switch.css';

    static formAssociated = true;

    #internals = null;
    #inputElement = null;
    #handleInputChangeBound = null;
    #handleBlurBound = null;
    #userErrorText = '';
    #internalErrorTextUpdate = false;

    constructor() {
        super();
        this.#internals = this.attachInternals();
    }

    async connectedCallback() {
        await super.connectedCallback();
        const culture = this.getAttribute('culture');
        if (culture) { i18n.setLocale(culture); }
        this.processInitialAttributesAfterRender(Switch.observedAttributes);
        this.setEventListeners();
        this.#applyTranslations();
    }

    async disconnectedCallback() {
        this.removeEventListeners();
        await super.disconnectedCallback();
    }

    static get observedAttributes() {
        return ['checked', 'disabled', 'required', 'error', 'error-text', 'supporting-text', 'state-text', 'name', 'culture'];
    }

    attributeChangedCallback(name, oldValue, newValue) {
        if (!this.isRendered) {
            return;
        }
        this.processAttribute(name, oldValue, newValue);
    }

    processAttribute(name, oldValue, newValue) {
        switch (name) {
            case 'checked':
                this.#updateChecked();
                this.#updateStateText();
                break;
            case 'disabled':
                this.#updateDisabled();
                break;
            case 'required':
                this.hasAttribute('required')
                    ? this.#inputElement.setAttribute('required', '')
                    : this.#inputElement.removeAttribute('required');
                break;
            case 'error':
                this.#updateError();
                break;
            case 'error-text':
                if (!this.#internalErrorTextUpdate) {
                    this.#userErrorText = (newValue || '').trim();
                }
                this.#updateMessages();
                break;
            case 'supporting-text':
                this.#updateMessages();
                break;
            case 'state-text':
                this.#updateStateText();
                break;
            case 'name':
                // name is used by internals for form submission; no inner element needs it
                break;
            case 'culture':
                if (newValue) {
                    i18n.setLocale(newValue);
                    requestAnimationFrame(() => {
                        this.#applyTranslations();
                        this.#updateStateText();
                    });
                }
                break;
            default:
                break;
        }
    }

    #updateChecked() {
        if (!this.#inputElement) {return;}
        const checked = this.hasAttribute('checked');
        this.#inputElement.checked = checked;
        this.#inputElement.setAttribute('aria-checked', checked.toString());
        const formValue = checked && this.getAttribute('name') ? 'on' : null;
        this.#internals.setFormValue?.(formValue);
    }

    #updateDisabled() {
        if (!this.#inputElement) {return;}
        const disabled = this.hasAttribute('disabled');
        this.#inputElement.disabled = disabled;
        this.#inputElement.setAttribute('aria-disabled', disabled.toString());
    }

    #updateError() {
        if (!this.#inputElement) {return;}
        this.hasAttribute('error')
            ? this.#inputElement.setAttribute('aria-invalid', 'true')
            : this.#inputElement.removeAttribute('aria-invalid');
        this.#updateMessages();
    }

    validateRequired() {
        const isChecked = this.hasAttribute('checked');
        const fallbackLabel = this.translate('SWITCH.DEFAULT_FIELD_LABEL');
        const labelText = (this.querySelector('[slot="label"]')?.textContent || fallbackLabel).trim();
        const requiredSuffix = this.translate('INPUT.VALIDATION.REQUIRED');
        const generatedErrorText = `${labelText} ${requiredSuffix}`.trim();
        const errorText = this.#userErrorText || generatedErrorText;

        this.#internalErrorTextUpdate = true;
        try {
            if (!isChecked) {
                this.setAttribute('error', '');
                this.setAttribute('error-text', errorText);
            } else {
                this.removeAttribute('error');
                this.removeAttribute('error-text');
            }
        } finally {
            this.#internalErrorTextUpdate = false;
        }

        this.#updateMessages();
    }

    setEventListeners() {
        this.#inputElement = this.shadowRoot?.querySelector('input[type="checkbox"]');
        this.#handleInputChangeBound = this.#handleInputChange.bind(this);
        this.#handleBlurBound = this.#handleBlur.bind(this);
        this.#inputElement?.addEventListener('change', this.#handleInputChangeBound);
        this.#inputElement?.addEventListener('blur', this.#handleBlurBound);
    }

    removeEventListeners() {
        this.#inputElement?.removeEventListener('change', this.#handleInputChangeBound);
        this.#inputElement?.removeEventListener('blur', this.#handleBlurBound);
        this.#inputElement = null;
        this.#handleInputChangeBound = null;
        this.#handleBlurBound = null;
    }

    #handleInputChange() {
        if (!this.#inputElement || this.#inputElement.disabled) {return;}

        const isChecked = this.#inputElement.checked;

        if (isChecked) {
            this.setAttribute('checked', '');
        } else {
            this.removeAttribute('checked');
        }

        if (this.hasAttribute('required')) {
            this.validateRequired();
        }

        this.dispatchEvent(new CustomEvent('neptune-switch-change', {
            bubbles: true,
            composed: true,
            detail: { checked: isChecked }
        }));
    }

    #handleBlur() {
        if (this.hasAttribute('required')) {
            this.validateRequired();
        }
    }

    #updateMessages() {
        const errorList = this.shadowRoot?.querySelector('ul');
        const supportingText = this.shadowRoot?.querySelector('div > div > span');

        if (!errorList || !supportingText) {return;}

        errorList.setAttribute('aria-live', 'polite');
        supportingText.setAttribute('aria-live', 'polite');

        if (this.hasAttribute('error')) {
            const errorText = this.getAttribute('error-text') || '';
            const errors = errorText.split('|').filter(e => e.trim());

            errorList.innerHTML = '';
            if (errors.length > 0) {
                errors.forEach(error => {
                    const li = document.createElement('li');
                    li.textContent = error.trim();
                    errorList.appendChild(li);
                });
                errorList.style.display = 'block';
            } else {
                errorList.style.display = 'none';
            }

            const supportingTextValue = this.getAttribute('supporting-text') || '';
            if (supportingTextValue) {
                supportingText.textContent = supportingTextValue;
                supportingText.classList.add('visually-hidden-a11y');
                supportingText.style.display = 'block';
            } else {
                supportingText.style.display = 'none';
            }
        } else {
            errorList.innerHTML = '';
            errorList.style.display = 'none';

            const supportingTextValue = this.getAttribute('supporting-text') || '';
            supportingText.textContent = supportingTextValue;
            supportingText.classList.remove('visually-hidden-a11y');
            supportingText.style.display = supportingTextValue ? 'block' : 'none';
        }
    }

    #parseStateText() {
        const attr = (this.getAttribute('state-text') || '').trim();
        const defaultCheckedLabel = this.translate('SWITCH.STATE_ON');
        const defaultUncheckedLabel = this.translate('SWITCH.STATE_OFF');

        if (!attr) {
            return [defaultCheckedLabel, defaultUncheckedLabel];
        }

        const inner = attr.replace(/^\s*\[/, '').replace(/\]\s*$/, '');
        const parts = inner.split(',').map(s => s.trim().replace(/^['"]|['"]$/g, ''));
        return [parts[0] || defaultCheckedLabel, parts[1] || defaultUncheckedLabel];
    }

    #updateStateText() {
        const stateTextElement = this.querySelector('[slot="state-text"]');
        if (!stateTextElement) {return;}
        const [checkedLabel, uncheckedLabel] = this.#parseStateText();
        const isChecked = this.hasAttribute('checked');
        stateTextElement.textContent = isChecked ? (checkedLabel || this.translate('SWITCH.STATE_ON')) : (uncheckedLabel || this.translate('SWITCH.STATE_OFF'));
    }

    /**
        * Applies translated content for the switch component.
        * Updates validation messages and state labels based on the current locale.
        * @returns {void}
    */
    #applyTranslations() {
        if (this.hasAttribute('required') && !this.hasAttribute('checked') && !this.#userErrorText) {
            this.validateRequired();
            return;
        }

        this.#updateMessages();
        this.#updateStateText();
    }

    /**
    * Re-applies translations when the active locale changes.
    * @returns {void}
    */
    onLocaleChange() {
        this.#applyTranslations();
    }

    get checked() {
        return this.hasAttribute('checked');
    }

    set checked(value) {
        if (value) {
            this.setAttribute('checked', '');
        } else {
            this.removeAttribute('checked');
        }
    }

    get disabled() {
        return this.hasAttribute('disabled');
    }

    set disabled(value) {
        if (value) {
            this.setAttribute('disabled', '');
        } else {
            this.removeAttribute('disabled');
        }
    }

    get required() {
        return this.hasAttribute('required');
    }

    set required(value) {
        if (value) {
            this.setAttribute('required', '');
        } else {
            this.removeAttribute('required');
        }
    }

    get error() {
        return this.hasAttribute('error');
    }

    set error(value) {
        if (value) {
            this.setAttribute('error', '');
        } else {
            this.removeAttribute('error');
        }
    }

    get errorText() {
        return this.getAttribute('error-text') || '';
    }

    set errorText(value) {
        if (value) {
            this.setAttribute('error-text', String(value));
        } else {
            this.removeAttribute('error-text');
        }
    }

    get supportingText() {
        return this.getAttribute('supporting-text') || '';
    }

    set supportingText(value) {
        if (value) {
            this.setAttribute('supporting-text', String(value));
        } else {
            this.removeAttribute('supporting-text');
        }
    }

    formResetCallback() {
        this.removeAttribute('checked');
        this.removeAttribute('error');
        this.removeAttribute('error-text');
        this.#internals.setFormValue?.(null);
    }

    formDisabledCallback(isDisabled) {
        if (isDisabled) {
            this.setAttribute('disabled', '');
        } else {
            this.removeAttribute('disabled');
        }
    }

    get validity() {
        return this.#internals.validity;
    }

    get validationMessage() {
        return this.#internals.validationMessage ?? '';
    }

    get willValidate() {
        return this.#internals.willValidate ?? false;
    }

    checkValidity() {
        return this.#internals.checkValidity?.() ?? true;
    }

    reportValidity() {
        return this.#internals.reportValidity?.() ?? true;
    }
}

customElements.define('neptune-switch', Switch);

export default Switch;
